import { PLAN_PRICES, MIN_ARTISTS, MAX_ARTISTS, type BillingInterval } from "../../../../../packages/domain/src/billing.ts";
export { MIN_ARTISTS, MAX_ARTISTS, ADDITIONAL_ARTIST_CENTS, YEARLY_DISCOUNT, quotePlan, type PlanId, type BillingInterval } from "../../../../../packages/domain/src/billing.ts";

const CORE_PLAN_FEATURES = [
  "All 3 styles, including Hatching",
  "Retouch with Draw, Erase & Restore",
  "Set your exact print dimensions",
  "PNG, PDF & JPEG downloads",
  "Multi-page PDFs for larger pieces",
  "Adjust line thickness & darkness",
  "Before & after comparison",
  "Focused reference mode",
  "Preview placement on a photo",
  "Custom ink colors",
  "Try sizes with fitting sheets",
] as const;

const ADVANCED_PLAN_FEATURES = [
  ...CORE_PLAN_FEATURES,
  "Version history & project backups",
  "24/7 support",
] as const;

export const PLANS = [
  {
    id: "hobby", name: "Hobby", description: "For side projects and big ideas.",
    ...PLAN_PRICES.hobby, action: "Choose Hobby", intro: "What’s included",
    features: ["20 stencil credits each month", ...CORE_PLAN_FEATURES],
  },
  {
    id: "premium", name: "Premium", description: "For artists with a busy schedule.",
    ...PLAN_PRICES.premium, action: "Go Premium", intro: "What’s included",
    features: ["100 stencil credits each month", ...ADVANCED_PLAN_FEATURES],
  },
  {
    id: "studio", name: "Studio", description: "For tattoo shops creating side by side.",
    ...PLAN_PRICES.studio, action: "Build your Studio", intro: "Included for every artist",
    features: ["120 stencil credits per artist / month", ...ADVANCED_PLAN_FEATURES],
  },
] as const;

export function creditIncreasePercent(credits: number, baselineCredits: number) {
  if (credits <= baselineCredits || baselineCredits <= 0) return 0;
  return Math.round((credits - baselineCredits) / baselineCredits * 100);
}

export function money(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 }).format(cents / 100);
}

export function readPricingSelection(search: string) {
  const params = new URLSearchParams(search);
  const rawArtists = Number(params.get("artists") ?? MIN_ARTISTS);
  const artists = Number.isInteger(rawArtists) && rawArtists >= MIN_ARTISTS && rawArtists <= MAX_ARTISTS ? rawArtists : MIN_ARTISTS;
  const interval: BillingInterval = params.get("billing") === "yearly" ? "yearly" : "monthly";
  const plan = PLANS.find(item => item.id === params.get("plan"))?.id ?? null;
  return { artists, interval, plan };
}

// Restrict auth return URLs to our own creator and landing-page pricing section. Keep the
// selected plan, interval, and artist count across sign-in and sign-up.
export function authDestination(from: unknown): string {
  if (typeof from !== "string") return "/ai";
  if (from === "/ai") return from;
  if (/^\/ai\?intake=[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u.test(from)) return from;
  if (from === "/billing" || from === "/#pricing") return "/#pricing";
  const legacyPricing = from.startsWith("/billing?");
  const landingPricing = from.startsWith("/?") && from.endsWith("#pricing");
  if (!legacyPricing && !landingPricing) return "/ai";
  const query = from.slice(from.indexOf("?"), from.indexOf("#") < 0 ? undefined : from.indexOf("#"));
  const { artists, interval, plan } = readPricingSelection(query);
  const params = new URLSearchParams({ billing: interval, artists: String(artists) });
  if (plan) params.set("plan", plan);
  if (plan && new URLSearchParams(query).get("checkout") === "start") params.set("checkout", "start");
  return `/?${params}#pricing`;
}
